import Backbone from 'backbone';

import AntibodyHistoryPanelView from 'antibodies/panels/AntibodyHistoryPanelView';
import ModelTypeConstants, {SequenceSubtype} from 'constants/ModelTypeConstants';
import BaseFolderItem from 'folder-items/BaseFolderItem';
import ComplexPolymerHistoryPanelView from 'modbio/complex-polymers/panels/ComplexPolymerHistoryPanelView';
import EntryHistoryPanelView from 'notebook/entries/backbone/panels/EntryHistoryPanelView';
import EntryInformationPanelView from 'notebook/entries/backbone/panels/EntryInformationPanelView';
import SubtemplateHistoryPanelView from 'notebook/entry-templates/backbone/panels/SubtemplateHistoryPanelView';
import SubtemplateInformationPanelView from 'notebook/entry-templates/backbone/panels/SubtemplateInformationPanelView';
import TemplateHistoryPanelView from 'notebook/entry-templates/backbone/panels/TemplateHistoryPanelView';
import TemplateInformationPanelView from 'notebook/entry-templates/backbone/panels/TemplateInformationPanelView';
import ProtocolHistoryPanelView from 'notebook/protocols/backbone/panels/ProtocolHistoryPanelView';
import ProtocolInformationPanelView from 'notebook/protocols/backbone/panels/ProtocolInformationPanelView';
import ProteinAlignmentPanelView from 'toolbar/aa-sequence/AaSequenceAlignmentPanelView';
import ProteinAnnotationsPanelView from 'toolbar/aa-sequence/AaSequenceAnnotationsPanelView';
import ProteinHistoryPanelView from 'toolbar/aa-sequence/AaSequenceHistoryPanelView';
import ProteinInformationPanelView from 'toolbar/aa-sequence/AaSequenceInformationPanelView';
import AlignmentPanelView from 'toolbar/AlignmentPanelView';
import FeaturePanelView from 'toolbar/feature-panel/FeaturePanelView';
import GenomeEngineeringPanelView from 'toolbar/GenomeEngineeringPanelView';
import MoleculeHistoryPanelView from 'toolbar/molecule/MoleculeHistoryPanelView';
import MoleculeInformationPanelView from 'toolbar/molecule/MoleculeInformationPanelView';
import PrimerPanelView from 'toolbar/primer-panel/PrimerPanelView';
import RestrictionPanelView from 'toolbar/restriction-panel/RestrictionPanelView';
import SequenceHistoryPanelView from 'toolbar/SequenceHistoryPanelView';
import SequenceInformationPanelView from 'toolbar/SequenceInformationPanelView';
import BasicInformationPanelView from 'toolbar/shared/BasicInformationPanelView';
import StageEntryHistoryPanelView from 'workflows/stage-entries/StageEntryHistoryPanelView';
import StageEntryInformationPanelView from 'workflows/stage-entries/StageEntryInformationPanelView';

export const SEQUENCE_INFORMATION_PANEL_INFO = 'information';
export const SEQUENCE_INFORMATION_PANEL_TAGS = 'tags';
export const SEQUENCE_PRIMER_PANEL_PRIMERS = 'primers';
export const SEQUENCE_PRIMER_PANEL_PAIRS = 'links';
export const SEQUENCE_DIGEST_PANEL_NEW_DIGEST = 'new-digest';
export const SEQUENCE_DIGEST_PANEL_DIGESTS = 'my-digests';
export const SEQUENCE_FEATURE_PANEL_ANNOTATIONS = 'annotations';
export const SEQUENCE_FEATURE_PANEL_TRANSLATIONS = 'translations';

export type ListenedPanel =
  | 'feature'
  | 'restriction'
  | 'primer'
  | 'alignment'
  | 'information'
  | 'history'
  | 'genomeEngineering';

export interface PanelNameToViewMapping {
  featurePanel: FeaturePanelView;
  restrictionPanel: RestrictionPanelView;
  primerPanel: PrimerPanelView;
  informationPanel: SequenceInformationPanelView;
  historyPanel: SequenceHistoryPanelView;
  alignmentPanel: AlignmentPanelView;
  genomeEngineeringPanel: GenomeEngineeringPanelView;
  entryInformationPanel: EntryInformationPanelView;
  entryHistoryPanel: EntryHistoryPanelView;
  protocolInformationPanel: ProtocolInformationPanelView;
  protocolHistoryPanel: ProtocolHistoryPanelView;
  subtemplateHistoryPanel: SubtemplateHistoryPanelView;
  subtemplateInformationPanel: SubtemplateInformationPanelView;
  templateHistoryPanel: TemplateHistoryPanelView;
  templateInformationPanel: TemplateInformationPanelView;
  proteinAnnotationsPanel: ProteinAnnotationsPanelView;
  proteinHistoryPanel: ProteinHistoryPanelView;
  proteinInformationPanel: ProteinInformationPanelView;
  proteinAlignmentPanel: ProteinAlignmentPanelView;
  basicFolderItemInformationPanel: BasicInformationPanelView<BaseFolderItem>;
  complexPolymerHistoryPanel: ComplexPolymerHistoryPanelView;
  stageEntryInformationPanel: StageEntryInformationPanelView;
  stageEntryHistoryPanel: StageEntryHistoryPanelView;
  moleculeHistoryPanel: MoleculeHistoryPanelView;
  moleculeInformationPanel: MoleculeInformationPanelView;
  antibodyHistoryPanel: AntibodyHistoryPanelView;
}

export interface PanelNameToIdMapping {
  featurePanel: 'feature-panel';
  restrictionPanel: 'restriction-panel';
  primerPanel: 'primer-panel';
  informationPanel: 'information-panel';
  historyPanel: 'history-panel';
  alignmentPanel: 'alignment-panel';
  genomeEngineeringPanel: 'genome-engineering-panel';
  entryInformationPanel: 'entry-information-panel';
  entryHistoryPanel: 'entry-history-panel';
  protocolInformationPanel: 'protocol-information-panel';
  protocolHistoryPanel: 'protocol-history-panel';
  subtemplateHistoryPanel: 'subtemplate-history-panel';
  subtemplateInformationPanel: 'subtemplate-information-panel';
  templateHistoryPanel: 'template-history-panel';
  templateInformationPanel: 'template-information-panel';
  basicFolderItemInformationPanel: 'basic-folder-item-information-panel';
  complexPolymerHistoryPanel: 'complex-polymer-history-panel';
  proteinAnnotationsPanel: 'protein-annotations-panel';
  proteinHistoryPanel: 'protein-history-panel';
  proteinInformationPanel: 'protein-information-panel';
  proteinAlignmentPanel: 'protein-alignment-panel';
  stageEntryInformationPanel: 'stage-entry-information-panel';
  stageEntryHistoryPanel: 'stage-entry-history-panel';
  moleculeInformationPanel: 'molecule-information-panel';
  moleculeHistoryPanel: 'molecule-history-panel';
  antibodyHistoryPanel: 'antibody-history-panel';
}

export type PanelName = keyof PanelNameToViewMapping;
export type PanelId = PanelNameToIdMapping[PanelName];

export type PanelViewModelClass<PName extends PanelName> =
  PanelNameToViewMapping[PName] extends Backbone.View<infer ModelClass> ? ModelClass : never;

// Perform some fun TS: this interface says that for each name in `PanelName` the interface must
// have that name with a "State" suffix map to the Backbone Model class used by the Backbone View class in
// `PanelMapping`. So for instance, "historyPanel" becomes "historyPanelState" and should map to `HistoryPanelState`
// since that is the Backbone Model used by `SequenceHistoryPanelView`.
export type PanelStateNameToModelMapping = {
  [PName in PanelName as `${PName}State`]: PanelViewModelClass<PName>;
};

// Do not read further if you value your sanity: this type means that for each name in `ListenedPanel`,
// `ListenedPanelMapping` contains a key of that name with a suffix of `-panel-ref`, mapping to a
// `PanelViewModelClass` called that name with a suffix of `Panel`.
export type ListenedPanelMapping = {
  [PName in ListenedPanel as `${PName}-panel-ref`]?: PanelViewModelClass<`${PName}Panel`>;
};

export type ModelTypeWithToolbar =
  | SequenceSubtype
  | ModelTypeConstants.ENTRY
  | ModelTypeConstants.PROTOCOL
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.SUBTEMPLATE
  | ModelTypeConstants.TEMPLATE
  | ModelTypeConstants.BASIC_FOLDER_ITEM
  | ModelTypeConstants.STAGE_ENTRY
  | ModelTypeConstants.MOLECULE;

export const TOOLBARS = {
  [ModelTypeConstants.SEQUENCE]: [
    'featurePanel',
    'restrictionPanel',
    'primerPanel',
    'informationPanel',
    'historyPanel',
    'alignmentPanel',
    'genomeEngineeringPanel',
  ],
  [ModelTypeConstants.RNA_SEQUENCE]: [
    'featurePanel',
    'restrictionPanel',
    'primerPanel',
    'informationPanel',
    'historyPanel',
    'alignmentPanel',
  ],
  [ModelTypeConstants.DNA_OLIGO]: ['featurePanel', 'informationPanel', 'historyPanel'],
  [ModelTypeConstants.RNA_OLIGO]: ['featurePanel', 'informationPanel', 'historyPanel'],
  [ModelTypeConstants.ENTRY]: ['entryInformationPanel', 'entryHistoryPanel'],
  [ModelTypeConstants.PROTOCOL]: ['protocolInformationPanel', 'protocolHistoryPanel'],
  [ModelTypeConstants.PROTEIN]: [
    'proteinAnnotationsPanel',
    'proteinHistoryPanel',
    'proteinInformationPanel',
    'proteinAlignmentPanel',
  ],
  [ModelTypeConstants.SUBTEMPLATE]: ['subtemplateInformationPanel', 'subtemplateHistoryPanel'],
  [ModelTypeConstants.TEMPLATE]: ['templateInformationPanel', 'templateHistoryPanel'],
  [ModelTypeConstants.BASIC_FOLDER_ITEM]: [
    'basicFolderItemInformationPanel',
    'complexPolymerHistoryPanel',
    'antibodyHistoryPanel',
  ],
  [ModelTypeConstants.STAGE_ENTRY]: ['stageEntryInformationPanel', 'stageEntryHistoryPanel'],
  [ModelTypeConstants.MOLECULE]: ['moleculeHistoryPanel', 'moleculeInformationPanel'],
} as const satisfies {[modelType in ModelTypeWithToolbar]: Array<PanelName>};
